import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import { setBootstrappedModules } from "./modulesBootstrapCache";

const MatterContext = createContext(null);

let bootstrapRequestInFlight = null;

const NO_OPEN_MATTER_STORAGE_KEY = "bhp:no_open_matter";
const PARTY_ID_STORAGE_KEY = "partyId";
const SELECTED_MATTER_STORAGE_KEY = "selectedMatterId";
const MATTER_ID_STORAGE_KEY = "matterId";

const setNoOpenMatterFlag = (value) => {
  try {
    if (value) {
      localStorage.setItem(NO_OPEN_MATTER_STORAGE_KEY, "true");
      sessionStorage.setItem(NO_OPEN_MATTER_STORAGE_KEY, "true");
      return;
    }
    localStorage.removeItem(NO_OPEN_MATTER_STORAGE_KEY);
    sessionStorage.removeItem(NO_OPEN_MATTER_STORAGE_KEY);
  } catch {}
};

const writeStorageValue = (key, value) => {
  try {
    if (value) {
      localStorage.setItem(key, value);
      sessionStorage.setItem(key, value);
      return;
    }
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  } catch {}
};

const getAuth = () => {
  const idToken =
    localStorage.getItem("idToken") || sessionStorage.getItem("idToken");
  const accessToken =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");
  const token = idToken || accessToken;
  const authFlow =
    localStorage.getItem("authFlow") || sessionStorage.getItem("authFlow");
  const clientId =
    localStorage.getItem("clientId") || sessionStorage.getItem("clientId");
  return { token, accessToken, authFlow, clientId };
};

const normalizePartyScope = (scope) => {
  const party = scope?.party || null;
  return {
    party,
    matters: Array.isArray(scope?.matters) ? scope.matters : [],
    coCounsel: scope?.coCounsel || null,
  };
};

const normalizeBootstrapPayload = ({ data, usePortalUserFlow }) => {
  const hasMultiPartyPayload = Array.isArray(data?.parties);

  if (hasMultiPartyPayload) {
    const partyScopes = data.parties.map(normalizePartyScope).filter((scope) => scope.party);
    return {
      ok: true,
      reason: partyScopes.length === 0 ? "no_open_matter" : "ready",
      partyScopes,
      modules: data?.modules,
    };
  }

  const matters = Array.isArray(data?.matters) ? data.matters : [];

  if (usePortalUserFlow && matters.length === 0) {
    return { ok: false, reason: "no_open_matter" };
  }

  return {
    ok: true,
    reason: "ready",
    partyScopes: data?.party
      ? [
          {
            party: data.party,
            matters,
            coCounsel: data?.coCounsel || null,
          },
        ]
      : [],
    modules: data?.modules,
  };
};

const fetchBootstrapPayload = async ({
  token,
  clientId,
  usePortalUserFlow,
  apiBaseUrl,
}) => {
  const url = usePortalUserFlow
    ? `${apiBaseUrl}/bhp-client-portal/me`
    : `${apiBaseUrl}/bhp-client-portal/clients/${encodeURIComponent(clientId)}`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (usePortalUserFlow && response.status === 404) {
    return { ok: false, reason: "no_open_matter" };
  }

  if (!response.ok) {
    return { ok: false, reason: "error" };
  }

  const data = await response.json();
  return normalizeBootstrapPayload({ data, usePortalUserFlow });
};

const getMatterId = (matter) => {
  return matter?.id || matter?.matterId || matter?.Id || null;
};

const getPartyId = (party) => {
  return party?.id || party?.partyId || party?.Id || null;
};

const buildInitialSelection = (partyScopes) => {
  if (!Array.isArray(partyScopes) || partyScopes.length === 0) {
    return { partyId: null, matterId: null };
  }

  if (partyScopes.length > 1) {
    return { partyId: null, matterId: null };
  }

  const onlyPartyScope = partyScopes[0];
  const onlyPartyId = getPartyId(onlyPartyScope?.party);
  const matters = Array.isArray(onlyPartyScope?.matters) ? onlyPartyScope.matters : [];

  if (matters.length !== 1) {
    return { partyId: onlyPartyId, matterId: null };
  }

  return {
    partyId: onlyPartyId,
    matterId: getMatterId(matters[0]),
  };
};

const updatePartyScopesForSelectedParty = (partyScopes, selectedPartyId, updater) => {
  return partyScopes.map((scope) => {
    if (getPartyId(scope?.party) !== selectedPartyId) {
      return scope;
    }
    const nextParty =
      typeof updater === "function" ? updater(scope.party || null) : updater;
    return {
      ...scope,
      party: nextParty || null,
    };
  });
};

const updatePartyScopesForSelectedMatter = (partyScopes, selectedPartyId, selectedMatterId, updater) => {
  return partyScopes.map((scope) => {
    if (getPartyId(scope?.party) !== selectedPartyId) {
      return scope;
    }

    const nextMatters = scope.matters.map((item) => {
      if (getMatterId(item) !== selectedMatterId) {
        return item;
      }
      return typeof updater === "function" ? updater(item || null) : updater;
    });

    return {
      ...scope,
      matters: nextMatters.filter(Boolean),
    };
  });
};

export const MatterProvider = ({ children }) => {
  const { isLoggedIn } = useAuth();
  const [partyScopes, setPartyScopes] = useState([]);
  const [selectedPartyId, setSelectedPartyId] = useState(null);
  const [selectedMatterId, setSelectedMatterId] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [bootstrapState, setBootstrapState] = useState("idle");

  useEffect(() => {
    if (isLoggedIn) return;
    setPartyScopes([]);
    setSelectedPartyId(null);
    setSelectedMatterId(null);
    setBootstrapping(false);
    setBootstrapState("idle");
    setNoOpenMatterFlag(false);
    writeStorageValue(PARTY_ID_STORAGE_KEY, null);
    writeStorageValue(SELECTED_MATTER_STORAGE_KEY, null);
    writeStorageValue(MATTER_ID_STORAGE_KEY, null);
    bootstrapRequestInFlight = null;
  }, [isLoggedIn]);

  const selectedPartyScope = useMemo(() => {
    if (!selectedPartyId) return null;
    return (
      partyScopes.find((scope) => getPartyId(scope?.party) === selectedPartyId) || null
    );
  }, [partyScopes, selectedPartyId]);

  const selectedMatter = useMemo(() => {
    if (!selectedPartyScope || !selectedMatterId) return null;
    return (
      selectedPartyScope.matters.find((item) => getMatterId(item) === selectedMatterId) || null
    );
  }, [selectedPartyScope, selectedMatterId]);

  useEffect(() => {
    if (!selectedPartyScope && selectedMatterId) {
      setSelectedMatterId(null);
      return;
    }

    if (
      selectedPartyScope &&
      selectedMatterId &&
      !selectedPartyScope.matters.some((item) => getMatterId(item) === selectedMatterId)
    ) {
      setSelectedMatterId(null);
    }
  }, [selectedPartyScope, selectedMatterId]);

  useEffect(() => {
    writeStorageValue(PARTY_ID_STORAGE_KEY, selectedPartyId);
  }, [selectedPartyId]);

  useEffect(() => {
    writeStorageValue(SELECTED_MATTER_STORAGE_KEY, selectedMatterId);
    writeStorageValue(MATTER_ID_STORAGE_KEY, selectedMatterId);
  }, [selectedMatterId]);

  const bootstrap = useCallback(async () => {
    if (bootstrapState === "no_open_matter") {
      return { ok: false, reason: "no_open_matter" };
    }

    const { token, authFlow, clientId } = getAuth();
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const usePortalUserFlow = authFlow === "portal_user";

    if (!API_BASE_URL || !token || (!usePortalUserFlow && !clientId)) {
      setBootstrapState("error");
      return { ok: false, reason: "error" };
    }

    setBootstrapping(true);
    setBootstrapState("loading");

    try {
      if (!bootstrapRequestInFlight) {
        bootstrapRequestInFlight = fetchBootstrapPayload({
          token,
          clientId,
          usePortalUserFlow,
          apiBaseUrl: API_BASE_URL,
        }).finally(() => {
          bootstrapRequestInFlight = null;
        });
      }

      const result = await bootstrapRequestInFlight;

      if (result?.reason === "no_open_matter") {
        setNoOpenMatterFlag(true);
        setBootstrappedModules([]);
        setPartyScopes([]);
        setSelectedPartyId(null);
        setSelectedMatterId(null);
        setBootstrapState("no_open_matter");
        return { ok: false, reason: "no_open_matter" };
      }

      if (!result?.ok) {
        setBootstrapState("error");
        return { ok: false, reason: "error" };
      }

      const nextPartyScopes = Array.isArray(result.partyScopes) ? result.partyScopes : [];
      const initialSelection = buildInitialSelection(nextPartyScopes);

      setNoOpenMatterFlag(false);
      if (usePortalUserFlow) {
        setBootstrappedModules(result.modules);
      }
      setPartyScopes(nextPartyScopes);
      setSelectedPartyId(initialSelection.partyId);
      setSelectedMatterId(initialSelection.matterId);
      setBootstrapState("ready");
      return { ok: true, reason: "ready" };
    } catch (err) {
      console.error("Matter bootstrap failed:", err);
      setNoOpenMatterFlag(false);
      setPartyScopes([]);
      setSelectedPartyId(null);
      setSelectedMatterId(null);
      setBootstrapState("error");
      return { ok: false, reason: "error" };
    } finally {
      setBootstrapping(false);
    }
  }, [bootstrapState]);

  const selectParty = useCallback(
    (partyId) => {
      const nextPartyId = partyId || null;
      const nextPartyScope =
        partyScopes.find((scope) => getPartyId(scope?.party) === nextPartyId) || null;

      setSelectedPartyId(nextPartyId);

      if (!nextPartyScope) {
        setSelectedMatterId(null);
        return;
      }

      if (nextPartyScope.matters.length === 1) {
        setSelectedMatterId(getMatterId(nextPartyScope.matters[0]));
        return;
      }

      setSelectedMatterId(null);
    },
    [partyScopes],
  );

  const selectMatter = useCallback((matterIdOrMatter) => {
    const nextMatterId =
      typeof matterIdOrMatter === "string"
        ? matterIdOrMatter
        : getMatterId(matterIdOrMatter);
    setSelectedMatterId(nextMatterId || null);
  }, []);

  const setParty = useCallback(
    (updater) => {
      if (!selectedPartyId) return;
      setPartyScopes((current) =>
        updatePartyScopesForSelectedParty(current, selectedPartyId, updater),
      );
    },
    [selectedPartyId],
  );

  const setMatter = useCallback(
    (updater) => {
      if (typeof updater !== "function") {
        selectMatter(updater);
        return;
      }
      if (!selectedPartyId || !selectedMatterId) return;
      setPartyScopes((current) =>
        updatePartyScopesForSelectedMatter(
          current,
          selectedPartyId,
          selectedMatterId,
          updater,
        ),
      );
    },
    [selectedMatterId, selectedPartyId, selectMatter],
  );

  const setMatters = useCallback(
    (updater) => {
      if (!selectedPartyId) return;
      setPartyScopes((current) =>
        current.map((scope) => {
          if (getPartyId(scope?.party) !== selectedPartyId) {
            return scope;
          }
          const nextMatters =
            typeof updater === "function"
              ? updater(Array.isArray(scope.matters) ? scope.matters : [])
              : updater;
          return {
            ...scope,
            matters: Array.isArray(nextMatters) ? nextMatters : [],
          };
        }),
      );
    },
    [selectedPartyId],
  );

  const setCoCounsel = useCallback(
    (updater) => {
      if (!selectedPartyId) return;
      setPartyScopes((current) =>
        current.map((scope) => {
          if (getPartyId(scope?.party) !== selectedPartyId) {
            return scope;
          }
          return {
            ...scope,
            coCounsel:
              typeof updater === "function" ? updater(scope.coCounsel || null) : updater,
          };
        }),
      );
    },
    [selectedPartyId],
  );

  const value = useMemo(
    () => ({
      parties: partyScopes,
      partyScopes,
      selectedPartyId,
      selectedMatterId,
      selectedPartyScope,
      selectParty,
      selectMatter,
      setSelectedPartyId: selectParty,
      setSelectedMatterId: selectMatter,
      party: selectedPartyScope?.party || null,
      matter: selectedMatter,
      matters: selectedPartyScope?.matters || [],
      coCounsel: selectedPartyScope?.coCounsel || null,
      setParty,
      setMatter,
      setMatters,
      setCoCounsel,
      bootstrapping,
      bootstrapState,
      bootstrap,
    }),
    [
      partyScopes,
      selectedPartyId,
      selectedMatterId,
      selectedPartyScope,
      selectParty,
      selectMatter,
      selectedMatter,
      setParty,
      setMatter,
      setMatters,
      setCoCounsel,
      bootstrapping,
      bootstrapState,
      bootstrap,
    ],
  );

  return (
    <MatterContext.Provider value={value}>{children}</MatterContext.Provider>
  );
};

export const useMatter = () => useContext(MatterContext);
